import { useLinkProps } from '@react-navigation/native';
import * as WebBrowser from 'expo-web-browser';
import React from 'react';
import { Pressable, View } from 'react-native';
import { Platform } from 'utils/platform';
import { useAnalytics } from 'services/analytics/useAnalytics';

export type WithLinkProps = {
  link?: string;
  component?: string;
  reference?: string;
  target?: number;
  requestTVFocus?: boolean;
  label?: string;
};

const withLink =
  <P extends object>(
    Component: React.ComponentType<P>,
    _componentName: string
  ): React.FC<P & WithLinkProps> =>
  ({ link, requestTVFocus, ...props }: P & WithLinkProps) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const androidTVFocusableButtonRef = React.useRef<View>(null);
    const analytics = useAnalytics();

    const triggerClickAnalytics = () => {
      const itemData = (props as any).item;
      const itemTitle = (props as any).title || itemData?.title || (props as any).label;
      const itemReference = (props as any).reference || itemData?.reference;

      const analyticsPayload = {
        programTitle: String(itemTitle),
        programListName: itemReference ? String(itemReference) : 'search'
      };

      if (itemTitle) analytics.triggerSelectProgramEvent(analyticsPayload);
    };

    if (!link) return <Component className="touchable" {...(props as P)} />;

     let processedLink = link
      .replace(/^(expo|bandplay)[^:]*:/, '')
      .replace(/^https?:\/\/(www\.)?bandplay\.com/, '')
      .replace(/^\/{2,}/, '/');

    if (/^https?:/.test(processedLink)) {
      return (
        <Pressable
          role="link"
          onPress={() => {
            triggerClickAnalytics();
            WebBrowser.openBrowserAsync(processedLink as string);
          }}>
          <Component {...(props as P)} />
        </Pressable>
      );
    } else if (processedLink.charAt(0) != '/') {
      processedLink = '/' + processedLink;
    }

    const { onPress: navigateAction } = useLinkProps({ to: processedLink });

    const handleInternalLinkPress = () => {
      triggerClickAnalytics();
      navigateAction();
    };

    if (Platform.OS === 'web') {
       return (
        <Pressable role="link" style={{ flex: 1 }} onPress={handleInternalLinkPress}>
          <Component {...(props as P)} />
        </Pressable>
      );
    }

    const onFocusProxy = () => setIsFocused(true);

    return (
      <Pressable
        role="link"
        onFocus={onFocusProxy}
        onPress={handleInternalLinkPress}
        ref={androidTVFocusableButtonRef}
        onBlur={() => setIsFocused(false)}
        hasTVPreferredFocus={requestTVFocus}
        style={{ borderWidth: 2, borderColor: isFocused ? 'red' : 'transparent' }}>
        <Component {...(props as P)} />
      </Pressable>
    );
  };

export default withLink;